import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Search, Calendar, Clock, Users, Eye, Pencil, Trash2, ClipboardList, BarChart3 } from 'lucide-react';
import ScheduleClient from '../data/api/ScheduleClient';
import { InterviewSchedule } from '../data/interfaces/ScheduleInterfaces';
import Loader from '../../../shared/components/Loader';
import Alert from '../../../shared/components/Alert';
import Modal from '../../../shared/components/Modal';
import AnimatedTabs from '../../../shared/components/AnimatedTabs';
import DateRangePicker from '../../../shared/components/DateRangePicker';
import { CustomDropdown } from '../../../shared/components/CustomDropdown';
import '../styles/ScheduleStyles.css';

type TabType = 'upcoming' | 'active' | 'closed';
const TABS: TabType[] = ['upcoming', 'active', 'closed'];

interface Round {
  round_id: string;
  name: string;
  description: string;
  seq_no: number;
  round_type: string;
}

/** Normalise a raw schedule record from the backend into the InterviewSchedule shape */
const normalise = (r: any): InterviewSchedule => ({
  id: r.id || r.schedule_id || '',
  sessionName: r.session_name || r.sessionName || '',
  roundType: r.round_type || r.roundType || '-',
  round_id: r.round_id || '',
  startDateTime: r.start_datetime || r.startDateTime || '',
  endDateTime: r.end_datetime || r.endDateTime || '',
  videoRecording: r.is_video_needed ?? r.videoRecording ?? false,
  duration: r.duration || 0,
  status: (r.status || 'upcoming').toLowerCase() as InterviewSchedule['status'],
  assignedUsers: r.target_users
    ? (r.target_users as string[]).map((email: string) => ({ id: email, name: email, email, role: '', department: '' }))
    : r.assignedUsers || [],
  target_users: r.target_users || [],
  createdAt: r.created_at || r.createdAt || '',
  interviewType: (r.interview_type || 'AI') as 'AI' | 'EXPERT',
  interviewerEmail: r.interviewer_email || r.interviewerEmail || '',
  meetingLink: r.meeting_link || r.meetingLink || '',
  jobDescription: r.job_description || r.jobDescription || '',
});

const InterviewSchedulePage: React.FC = () => {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState<InterviewSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('upcoming');
  const [searchQuery, setSearchQuery] = useState('');
  const [roundIdFilter, setRoundIdFilter] = useState('');
  const [rounds, setRounds] = useState<Round[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [deleteAlert, setDeleteAlert] = useState<{ show: boolean; scheduleId: string; scheduleName: string }>({
    show: false, scheduleId: '', scheduleName: '',
  });
  const [infoAlert, setInfoAlert] = useState({ show: false, title: '', message: '', type: 'success' as 'success' | 'error' });

  // Reset pagination whenever filters change
  useEffect(() => {
    setPage(1);
    setSchedules([]);
    setHasMore(true);
  }, [activeTab, searchQuery, roundIdFilter]);

  const getOffsetTime = () => {
    const offset = new Date().getTimezoneOffset();
    const sign = offset > 0 ? '-' : '+';
    const abs = Math.abs(offset);
    return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
  };

  useEffect(() => {
    const load = async () => {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);

      try {
        const params: any = {
          page_number: page,
          limit: 10,
          status: activeTab.charAt(0).toUpperCase() + activeTab.slice(1),
          offset_time: getOffsetTime(),
        };
        if (searchQuery) params.session_name = searchQuery;
        if (roundIdFilter) params.round_id = roundIdFilter;

        const [data, roundsData] = await Promise.all([
          ScheduleClient.list(params),
          page === 1 ? ScheduleClient.listRounds() : Promise.resolve(null),
        ]);

        const rawRecords = Array.isArray(data) ? data : data?.records || [];
        const records = rawRecords.map(normalise);
        setTotalCount(data?.total_no_of_records ?? data?.totalCount ?? rawRecords.length);

        if (page === 1) {
          setSchedules(records);
        } else {
          setSchedules(prev => [...prev, ...records]);
        }

        if (rawRecords.length < 10) setHasMore(false);

        if (roundsData) {
          const fetchedRounds: Round[] = (roundsData?.rounds || []).sort(
            (a: Round, b: Round) => a.seq_no - b.seq_no
          );
          setRounds(fetchedRounds);
        }
      } catch (err) {
        console.error('Failed to load schedules:', err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };

    if (hasMore || page === 1) load();
  }, [page, activeTab, searchQuery, roundIdFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50 && !loading && !loadingMore && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await ScheduleClient.delete(deleteAlert.scheduleId);
      setSchedules(prev => prev.filter(s => s.id !== deleteAlert.scheduleId));
      setTotalCount(prev => Math.max(0, prev - 1));
      setInfoAlert({ show: true, title: 'Deleted', message: 'Schedule deleted successfully.', type: 'success' });
    } catch {
      setInfoAlert({ show: true, title: 'Error', message: 'Failed to delete the schedule.', type: 'error' });
    } finally {
      setDeleting(false);
      setDeleteAlert({ show: false, scheduleId: '', scheduleName: '' });
    }
  }, [deleteAlert.scheduleId]);

  const roundOptions = useMemo(() => [
    { value: '', label: 'All Rounds' },
    ...rounds.map(r => ({ value: r.round_id, label: r.name })),
  ], [rounds]);

  // Client-side date-range filter only (round and status go to the API)
  const filteredSchedules = useMemo(() => {
    return schedules.filter(s => {
      if (dateFrom && new Date(s.startDateTime) < new Date(dateFrom)) return false;
      if (dateTo && new Date(s.startDateTime) > new Date(dateTo + 'T23:59:59')) return false;
      return true;
    });
  }, [schedules, dateFrom, dateTo]);

  const formatDateTime = (raw: string) => {
    if (!raw) return '-';
    // Backend may return "YYYY/MM/DD, HH:mm:ss" — normalise to ISO before parsing
    const iso = raw.replace(/\//g, '-').replace(', ', 'T');
    const d = new Date(iso);
    if (isNaN(d.getTime())) return raw;
    return (
      d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ', ' +
      d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    );
  };

  return (
    <motion.div className="learner-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
      <Loader isLoading={loading || deleting} message={deleting ? "Deleting schedule..." : "Loading schedules..."} />

      {/* Delete confirmation */}
      <Modal open={deleteAlert.show} onClose={() => setDeleteAlert({ show: false, scheduleId: '', scheduleName: '' })} title="Delete Schedule" width="420px">
        <p>Are you sure you want to delete this schedule? This action cannot be undone.</p>
        {deleteAlert.show && (
          <p style={{ fontWeight: 600, marginTop: 8, color: '#0f172a' }}>
            {deleteAlert.scheduleName}
          </p>
        )}
        <div className="modal-actions">
          <button className="btn-cancel" onClick={() => setDeleteAlert({ show: false, scheduleId: '', scheduleName: '' })}>Cancel</button>
          <button className="btn-danger" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </Modal>

      {/* Info / success alert */}
      <Alert
        show={infoAlert.show}
        title={infoAlert.title}
        message={infoAlert.message}
        type={infoAlert.type}
        onClose={() => setInfoAlert(prev => ({ ...prev, show: false }))}
        primaryButtonText="OK"
        onPrimaryClick={() => setInfoAlert(prev => ({ ...prev, show: false }))}
      />

      {/* ── Hero ── */}
      <div className="lp-hero" style={{ flexDirection: 'column', gap: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
          <div className="lp-hero-left">
            <h1>Interview Schedules</h1>
            <span className="lp-hero-chip">Manage upcoming, active, and closed sessions in one place.</span>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          {/* Total count card */}
          <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px 20px', gap: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ width: 40, height: 40, borderRadius: '10px', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Calendar size={20} strokeWidth={2.5} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1rem', color: '#64748b', fontWeight: 600, textTransform: 'none', letterSpacing: '0.5px' }}>Total Schedules:</span>
              <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', lineHeight: 1, letterSpacing: '-0.5px' }}>{totalCount}</span>
            </div>
          </div>

          {/* Status tabs */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <AnimatedTabs
              variant="line"
              size="md"
              tabs={TABS.map(t => ({
                label: t.charAt(0).toUpperCase() + t.slice(1),
                count: activeTab === t ? totalCount : undefined,
              }))}
              activeTab={activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
              onChange={label => setActiveTab(label.toLowerCase() as TabType)}
            />
          </div>

          {/* CTA */}
          <div className="lp-hero-right" style={{ gap: '12px', display: 'flex', alignItems: 'center' }}>
            <button className="btn-primary" onClick={() => navigate('/dashboard/interviews/schedule')}>
              <Plus size={18} />
              Schedule Interview
            </button>
          </div>
        </div>
      </div>

      {/* ── Table Panel ── */}
      <div className="lp-table-panel" style={{ marginTop: '16px' }}>
        {/* Toolbar */}
        <div className="lp-table-panel-head" style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.3px' }}>
              {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Schedules
            </h2>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {/* Search */}
            <div className="lp-search" style={{ height: 40, width: 260, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', position: 'relative' }}>
              <Search size={16} color="#94a3b8" />
              <input
                type="text"
                placeholder="Search by session name..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ paddingRight: searchQuery ? '30px' : '10px' }}
              />
              {searchQuery && (
                <button
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: '#f1f5f9', cursor: 'pointer', color: '#64748b', width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                  onClick={() => setSearchQuery('')}
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}
            </div>

            {/* Round Filter — sent to API */}
            <div style={{ minWidth: '180px' }}>
              <CustomDropdown
                options={roundOptions}
                value={roundIdFilter}
                onChange={setRoundIdFilter}
                placeholder="All Rounds"
              />
            </div>

            {/* Date Range — client-side only */}
            <DateRangePicker
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFromChange={setDateFrom}
              onDateToChange={setDateTo}
            />
          </div>
        </div>

        {/* Table */}
        <div className="lp-table-scroll-area" onScroll={handleScroll} style={{ maxHeight: '600px' }}>
          {filteredSchedules.length === 0 && !loading ? (
            <div className="is-empty">
              <Calendar size={48} color="#cbd5e1" style={{ margin: '0 auto' }} />
              <h3>No {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Schedules</h3>
              <p>There are no interview schedules matching your criteria.</p>
            </div>
          ) : (
            <>
              <table className="learner-table">
                <thead>
                  <tr>
                    <th>Session Name</th>
                    <th>Round Type</th>
                    <th>Type</th>
                    <th>Start Date &amp; Time</th>
                    <th>Total Participants</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSchedules.map(schedule => (
                    <tr key={schedule.id}>
                      <td>
                        <strong style={{ color: '#0f172a' }}>{schedule.sessionName || '-'}</strong>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', color: '#475569' }}>
                          {schedule.roundType || '-'}
                        </div>
                      </td>
                      <td>
                        <span className={`is-type-badge ${schedule.interviewType === 'EXPERT' ? 'expert' : 'ai'}`}>
                          {schedule.interviewType === 'EXPERT' ? 'Expert Driven' : 'AI Driven'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                          <Clock size={14} color="#94a3b8" />
                          {formatDateTime(schedule.startDateTime)}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                          <Users size={14} color="#94a3b8" />
                          {schedule.target_users?.length ?? schedule.assignedUsers?.length ?? 0} Participant{(schedule.target_users?.length ?? schedule.assignedUsers?.length ?? 0) !== 1 ? 's' : ''}
                        </div>
                      </td>
                      <td>
                        <div className="actions-row">
                          {schedule.status !== 'upcoming' && (
                            <button
                              className="action-btn results"
                              data-tooltip="View Results"
                              onClick={() => navigate(`/dashboard/results/learners`, { state: { scheduleId: schedule.id } })}
                            >
                              <BarChart3 size={18} />
                            </button>
                          )}
                          
                          <button
                            className="action-btn view"
                            data-tooltip="View Details"
                            onClick={() => navigate(`/dashboard/interviews/schedule`, { state: { id: schedule.id, view: true } })}
                          >
                            <Eye size={18} />
                          </button>
                          
                          {schedule.status !== 'closed' && (
                            <button
                              className="action-btn delete"
                              data-tooltip="Delete"
                              onClick={() => setDeleteAlert({ show: true, scheduleId: schedule.id, scheduleName: schedule.sessionName })}
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                          
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {loadingMore && (
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <span style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading more schedules…</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default InterviewSchedulePage;
